import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import SectionEditor, { clearSectionStateCache } from '../components/editor/SectionEditor';
import EditorToolbar from '../components/editor/EditorToolbar';
import ToolBar from '../components/editor/ToolBar';
import StatusBar from '../components/editor/StatusBar';
import FloatingPolishButton from '../components/editor/FloatingPolishButton';
import { useDocument } from '../hooks/useDocument';
import { useAutoSave } from '../hooks/useAutoSave';

// Slug for heading IDs
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s가-힣-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

// Extract headings from markdown content for TOC
function extractHeadings(text) {
  if (!text) return [];
  const headings = [];
  const lines = text.split('\n');
  let inCodeBlock = false;
  let lineIndex = 0;
  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      lineIndex++;
      continue;
    }
    if (inCodeBlock) { lineIndex++; continue; }
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      headings.push({
        level: match[1].length,
        text: match[2].trim(),
        id: slugify(match[2].trim()),
        lineIndex,
      });
    }
    lineIndex++;
  }
  return headings;
}

// Split content into sections at h1/h2 headings (character offset based)
function splitIntoSections(text) {
  if (!text) return [{ content: '', charStart: 0, charEnd: 0 }];

  const lines = text.split('\n');
  const sections = [];
  let charOffset = 0;
  let sectionCharStart = 0;
  let inCodeBlock = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
    }
    if (!inCodeBlock && /^#{1,2}\s/.test(line) && i > 0) {
      sections.push({
        content: text.substring(sectionCharStart, charOffset),
        charStart: sectionCharStart,
        charEnd: charOffset,
      });
      sectionCharStart = charOffset;
    }
    charOffset += line.length + 1;
  }

  sections.push({
    content: text.substring(sectionCharStart),
    charStart: sectionCharStart,
    charEnd: text.length,
  });

  return sections;
}

// ReactMarkdown components (same as ProjectDocViewer)
const markdownComponents = {
  h1: ({ children }) => <h1 id={slugify(String(children))}>{children}</h1>,
  h2: ({ children }) => <h2 id={slugify(String(children))}>{children}</h2>,
  h3: ({ children }) => <h3 id={slugify(String(children))}>{children}</h3>,
  h4: ({ children }) => <h4 id={slugify(String(children))}>{children}</h4>,
  h5: ({ children }) => <h5 id={slugify(String(children))}>{children}</h5>,
  h6: ({ children }) => <h6 id={slugify(String(children))}>{children}</h6>,
  code: ({ inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const lang = match ? match[1] : '';
    if (!inline && (lang || String(children).includes('\n'))) {
      return (
        <SyntaxHighlighter
          style={oneLight}
          language={lang || 'text'}
          PreTag="div"
          customStyle={{
            margin: '12px 0',
            borderRadius: '8px',
            fontSize: '13px',
            border: '1px solid #E8E8E8',
          }}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    }
    return <code className="pdv-inline-code" {...props}>{children}</code>;
  },
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="pdv-link">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="pdv-table-wrap">
      <table>{children}</table>
    </div>
  ),
  li: ({ children, className, ...props }) => {
    const isChecked = props.checked;
    if (typeof isChecked === 'boolean') {
      return (
        <li className="pdv-checklist-item">
          <input type="checkbox" checked={isChecked} readOnly />
          <span>{children}</span>
        </li>
      );
    }
    return <li {...props}>{children}</li>;
  },
  img: ({ src, alt }) => (
    <img src={src} alt={alt || ''} className="pdv-img" loading="lazy" />
  ),
  blockquote: ({ children }) => {
    // Check for GitHub-style callout syntax: [!INFO], [!WARNING], [!TIP]
    // ReactMarkdown renders: <blockquote><p>[!INFO]<br>content</p></blockquote>
    // So children = ['\n', <p>, '\n'] and p.children = ['[!INFO]', <br>, 'content']
    const childArray = Array.isArray(children) ? children : [children];
    const pElement = childArray.find(c => c?.type === 'p' || c?.props);
    if (pElement?.props?.children) {
      const pChildren = Array.isArray(pElement.props.children) ? pElement.props.children : [pElement.props.children];
      const firstText = pChildren.find(c => typeof c === 'string');
      if (typeof firstText === 'string') {
        const calloutMatch = firstText.match(/^\[!(INFO|WARNING|TIP|NOTE|CAUTION|IMPORTANT)\]\s*/i);
        if (calloutMatch) {
          const type = calloutMatch[1].toLowerCase();
          const icons = { info: 'ℹ️', warning: '⚠️', tip: '💡', note: '📝', caution: '🚨', important: '❗' };
          const labels = { info: '정보', warning: '경고', tip: '팁', note: '노트', caution: '주의', important: '중요' };
          // Remove callout marker and <br> after it from content
          const contentParts = pChildren.filter(c => c !== firstText && !(c?.type === 'br'));
          const remaining = firstText.slice(calloutMatch[0].length);
          return (
            <div className={`pdv-callout pdv-callout-${type}`}>
              <div className="pdv-callout-title">{icons[type] || 'ℹ️'} {labels[type] || type}</div>
              <div className="pdv-callout-content">
                {remaining && <p>{remaining}</p>}
                {contentParts.length > 0 && <p>{contentParts}</p>}
              </div>
            </div>
          );
        }
      }
    }
    return <blockquote className="pdv-blockquote">{children}</blockquote>;
  },
};

const Editor = ({ onSaved, onDelete, onPin, onDuplicate, onExport, isPinned, allTags }) => {
  const { id } = useParams();
  const { document, loading, error, saveStatus, setSaveStatus, load, save } = useDocument();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // View mode: 'read' or 'edit'
  const [viewMode, setViewMode] = useState('read');

  // Inline editing: which section is being edited (-1 = none)
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const editingContentRef = useRef('');
  const editingSectionRef = useRef(null);
  const [activeEditorView, setActiveEditorView] = useState(null);

  // Discarded draft: stored when user exits without saving
  const discardedDraftRef = useRef(null); // { content, title, tags }
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  // TOC state
  const [showToc, setShowToc] = useState(() => {
    const saved = localStorage.getItem('mb_editor_showToc');
    return saved !== null ? saved === 'true' : true;
  });
  const [tocWidth, setTocWidth] = useState(() => {
    const saved = localStorage.getItem('mb_editor_tocWidth');
    return saved ? parseInt(saved, 10) : 180;
  });

  useEffect(() => { localStorage.setItem('mb_editor_showToc', String(showToc)); }, [showToc]);
  useEffect(() => { localStorage.setItem('mb_editor_tocWidth', String(tocWidth)); }, [tocWidth]);

  const headings = useMemo(() => extractHeadings(content), [content]);
  const sections = useMemo(() => splitIntoSections(content), [content]);

  // Auto-save
  const doSave = useCallback(async () => {
    if (!document?.id) return;
    const result = await save({ title, content, tags });
    if (result) {
      setLastSavedAt(result.updated_at || new Date().toISOString());
      if (onSaved) {
        onSaved({ id: document.id, title, content, tags, updated_at: result.updated_at || new Date().toISOString() });
      }
    }
  }, [document?.id, title, content, tags, save, onSaved]);

  const { trigger: triggerAutoSave, flush: flushSave, isPending: isAutoSavePending } = useAutoSave(doSave, 2000);

  // Load document
  useEffect(() => {
    if (id) {
      load(id);
      setInitialized(false);
      setViewMode('read');
      setEditingSectionIdx(null);
      clearSectionStateCache();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync loaded document to local state
  useEffect(() => {
    if (document && !initialized) {
      setTitle(document.title || '');
      setContent(document.content || '');
      setLastSavedAt(document.updated_at || null);
      let parsedTags = [];
      if (Array.isArray(document.tags)) {
        parsedTags = document.tags;
      } else if (typeof document.tags === 'string') {
        try { parsedTags = JSON.parse(document.tags); } catch { parsedTags = []; }
      }
      setTags(parsedTags);
      setInitialized(true);
    }
  }, [document, initialized]);

  const handleTitleChange = useCallback((e) => {
    setTitle(e.target.value);
    setSaveStatus('unsaved');
    triggerAutoSave();
  }, [setSaveStatus, triggerAutoSave]);

  const handleTagsChange = useCallback((newTags) => {
    setTags(newTags);
    setSaveStatus('unsaved');
    triggerAutoSave();
  }, [setSaveStatus, triggerAutoSave]);

  const handleManualSave = useCallback(async () => {
    if (!document?.id) return;
    if (isAutoSavePending()) {
      await flushSave();
    } else {
      const result = await save({ title, content, tags });
      if (result) {
        setLastSavedAt(result.updated_at || new Date().toISOString());
        if (onSaved) {
          onSaved({ id: document.id, title, content, tags, updated_at: result.updated_at || new Date().toISOString() });
        }
      }
    }
  }, [isAutoSavePending, flushSave, document?.id, save, title, content, tags, onSaved]);

  const handleDelete = useCallback(() => {
    if (onDelete && document?.id) {
      onDelete(document.id);
    }
  }, [onDelete, document?.id]);

  // --- Section inline editing ---

  const commitSectionEdit = useCallback(() => {
    if (editingSectionIdx === null) return;
    const section = sections[editingSectionIdx];
    if (!section) return;
    const newContent = content.substring(0, section.charStart) + editingContentRef.current + content.substring(section.charEnd);
    setContent(newContent);
    setSaveStatus('unsaved');
    triggerAutoSave();
    setEditingSectionIdx(null);
    editingContentRef.current = '';
  }, [editingSectionIdx, sections, content, setSaveStatus, triggerAutoSave]);

  // AI Polish: replace original text with polished text in document
  const handleAiPolished = useCallback((original, polished) => {
    const idx = content.indexOf(original);
    if (idx === -1) return;
    const newContent = content.substring(0, idx) + polished + content.substring(idx + original.length);
    setContent(newContent);
    setSaveStatus('unsaved');
    triggerAutoSave();
  }, [content, setSaveStatus, triggerAutoSave]);

  const startEditingSection = useCallback((idx) => {
    if (editingSectionIdx !== null && editingSectionIdx !== idx) {
      commitSectionEdit();
    }
    editingContentRef.current = sections[idx]?.content || '';
    setEditingSectionIdx(idx);
  }, [editingSectionIdx, sections, commitSectionEdit]);

  const handleSectionChange = useCallback((newContent) => {
    editingContentRef.current = newContent;
  }, []);

  const handleSectionExit = useCallback(() => {
    commitSectionEdit();
  }, [commitSectionEdit]);

  // Scroll editing section into view after mount
  useEffect(() => {
    if (editingSectionIdx !== null && editingSectionRef.current) {
      requestAnimationFrame(() => {
        editingSectionRef.current?.scrollIntoView({ behavior: 'instant', block: 'nearest' });
      });
    }
  }, [editingSectionIdx]);

  // TOC
  const handleTocResizeStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = tocWidth;
    const onMove = (ev) => {
      const diff = startX - ev.clientX;
      setTocWidth(Math.max(120, Math.min(400, startW + diff)));
    };
    const onUp = () => {
      window.document.removeEventListener('mousemove', onMove);
      window.document.removeEventListener('mouseup', onUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };
    window.document.body.style.cursor = 'ew-resize';
    window.document.body.style.userSelect = 'none';
    window.document.addEventListener('mousemove', onMove);
    window.document.addEventListener('mouseup', onUp);
  }, [tocWidth]);

  const handleTocClick = useCallback((heading) => {
    // First try normal DOM lookup (works for ReactMarkdown-rendered sections)
    const el = window.document.getElementById(heading.id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // If not found, the heading is likely inside the CodeMirror editing section
    // Find the line in CodeMirror and scroll to it
    if (activeEditorView && editingSectionIdx !== null) {
      const doc = activeEditorView.state.doc;
      const headingPattern = heading.text;
      for (let i = 1; i <= doc.lines; i++) {
        const line = doc.line(i);
        const match = line.text.match(/^#{1,6}\s+(.+)$/);
        if (match && match[1].trim() === headingPattern) {
          activeEditorView.dispatch({
            effects: activeEditorView.scrollSnapshot ? undefined : undefined,
            selection: { anchor: line.from },
          });
          const coords = activeEditorView.coordsAtPos(line.from);
          if (coords) {
            const scrollContainer = editingSectionRef.current?.closest('.pdv-content');
            if (scrollContainer) {
              const containerRect = scrollContainer.getBoundingClientRect();
              scrollContainer.scrollBy({ top: coords.top - containerRect.top - 60, behavior: 'smooth' });
            }
          }
          return;
        }
      }
    }
  }, [activeEditorView, editingSectionIdx]);

  // Switch modes
  const switchToEdit = useCallback(() => {
    if (discardedDraftRef.current) {
      setShowRestorePrompt(true);
    } else {
      setViewMode('edit');
      // Auto-start editing first section so toolbar is immediately active
      if (sections.length > 0) {
        editingContentRef.current = sections[0]?.content || '';
        setEditingSectionIdx(0);
      }
    }
  }, [sections]);

  const handleRestoreDraft = useCallback(() => {
    const draft = discardedDraftRef.current;
    if (draft) {
      setContent(draft.content);
      setTitle(draft.title);
      setTags(draft.tags);
      setSaveStatus('unsaved');
      triggerAutoSave();
    }
    discardedDraftRef.current = null;
    setShowRestorePrompt(false);
    setViewMode('edit');
    // Auto-start editing first section
    if (sections.length > 0) {
      editingContentRef.current = sections[0]?.content || '';
      setEditingSectionIdx(0);
    }
  }, [setSaveStatus, triggerAutoSave, sections]);

  const handleDiscardDraft = useCallback(() => {
    discardedDraftRef.current = null;
    setShowRestorePrompt(false);
    setViewMode('edit');
    // Auto-start editing first section
    if (sections.length > 0) {
      editingContentRef.current = sections[0]?.content || '';
      setEditingSectionIdx(0);
    }
  }, [sections]);

  const switchToRead = useCallback(async () => {
    if (editingSectionIdx !== null) commitSectionEdit();
    if (isAutoSavePending()) await flushSave();
    setActiveEditorView(null);
    setViewMode('read');
  }, [editingSectionIdx, commitSectionEdit, isAutoSavePending, flushSave]);

  const isReadMode = viewMode === 'read';
  const isEditing = editingSectionIdx !== null;

  // Exit confirm dialog state
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Check if there are uncommitted section changes
  const hasUnsavedChanges = useCallback(() => {
    if (saveStatus === 'unsaved' || isAutoSavePending()) return true;
    if (editingSectionIdx !== null) {
      const section = sections[editingSectionIdx];
      if (section && editingContentRef.current !== section.content) return true;
    }
    return false;
  }, [saveStatus, isAutoSavePending, editingSectionIdx, sections]);

  // Exit: no changes → straight out, changes → show dialog
  const handleExitEdit = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowExitConfirm(true);
    } else {
      setActiveEditorView(null);
      setViewMode('read');
    }
  }, [hasUnsavedChanges]);

  // Save and exit
  const handleExitSave = useCallback(async () => {
    if (editingSectionIdx !== null) commitSectionEdit();
    await flushSave();
    discardedDraftRef.current = null;
    setShowExitConfirm(false);
    setActiveEditorView(null);
    setViewMode('read');
  }, [editingSectionIdx, commitSectionEdit, flushSave]);

  // Discard and exit — save draft for possible restore
  const handleExitDiscard = useCallback(() => {
    // Build the full content with uncommitted section edit applied
    let draftContent = content;
    if (editingSectionIdx !== null) {
      const section = sections[editingSectionIdx];
      if (section) {
        draftContent = content.substring(0, section.charStart) + editingContentRef.current + content.substring(section.charEnd);
      }
      editingContentRef.current = '';
      setEditingSectionIdx(null);
    }
    // Store draft for restore prompt
    discardedDraftRef.current = { content: draftContent, title, tags: [...tags] };
    setShowExitConfirm(false);
    setActiveEditorView(null);
    // Reload saved version
    if (document?.id) load(document.id);
    setViewMode('read');
  }, [editingSectionIdx, sections, content, title, tags, document?.id, load]);

  const formatTime = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      hour12: false,
    });
  };

  // --- Determine what to render for each section in edit mode ---
  // When no section is being edited: render single ReactMarkdown (identical to read mode)
  // When a section IS being edited: render before (ReactMarkdown) + editing (CodeMirror) + after (ReactMarkdown)
  const renderEditContent = () => {
    if (!isEditing) {
      // No section selected yet — show same ReactMarkdown as read mode, but with click zones
      return (
        <div className="pdv-content inline-edit-clickable" style={{ flex: 1, overflow: 'auto', padding: '0 24px 48px' }}>
          {sections.map((section, idx) => (
            <div
              key={`zone-${idx}-${section.charStart}`}
              className="editor-section hoverable"
              onClick={() => startEditingSection(idx)}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {section.content || ''}
              </ReactMarkdown>
            </div>
          ))}
        </div>
      );
    }

    // A section is being edited — render all sections, only the active one as CodeMirror
    return (
      <div className="pdv-content inline-edit-clickable" style={{ flex: 1, overflow: 'auto', padding: '0 24px 48px' }}>
        {sections.map((section, idx) => {
          if (idx === editingSectionIdx) {
            return (
              <div key={`edit-${idx}-${section.charStart}`} className="editor-section editing" ref={editingSectionRef}>
                <SectionEditor
                  content={section.content}
                  onChange={handleSectionChange}
                  onSave={handleManualSave}
                  onExit={handleSectionExit}
                  onViewReady={setActiveEditorView}
                  sectionKey={`section-${idx}`}
                />
              </div>
            );
          }
          return (
            <div
              key={`zone-${idx}-${section.charStart}`}
              className="editor-section hoverable"
              onClick={() => startEditingSection(idx)}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {section.content || ''}
              </ReactMarkdown>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading && !initialized) {
    return <div className="bear-editor-loading"><span>Loading...</span></div>;
  }

  if (error) {
    return <div className="bear-editor-loading"><span className="text-red-500">{error}</span></div>;
  }

  return (
    <div className="bear-editor">
      {/* Header */}
      <div className={`bear-editor-header ${!isReadMode ? 'editing' : ''}`}>
        <div className="bear-title-area">
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder="Title"
            className={`bear-title-input ${isReadMode ? 'bear-title-readonly' : ''}`}
            readOnly={isReadMode}
          />
          <button
            className={`bear-mode-toggle ${isReadMode ? 'read' : 'edit'}`}
            onClick={isReadMode ? switchToEdit : switchToRead}
            title={isReadMode ? '편집 모드' : '업데이트'}
          >
            {isReadMode ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
            <span>{isReadMode ? '편집' : '업데이트'}</span>
          </button>
          {!isReadMode && (
            <button
              className="bear-mode-toggle exit"
              onClick={handleExitEdit}
              title="편집 종료"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18" /><path d="M6 6l12 12" />
              </svg>
              <span>나가기</span>
            </button>
          )}
        </div>
        <div className="bear-editor-meta">
          {lastSavedAt && <span className="bear-editor-meta-item">수정: {formatTime(lastSavedAt)}</span>}
          <span className="bear-editor-meta-item">{(content || '').length.toLocaleString()}자</span>
        </div>
      </div>

      {/* EditorToolbar - always mounted, hidden in read mode */}
      <div className={`editor-toolbar-slide ${isReadMode ? 'hidden' : ''}`}>
        <EditorToolbar editorView={activeEditorView} onRequestEdit={() => {
          if (editingSectionIdx === null && sections.length > 0) {
            editingContentRef.current = sections[0]?.content || '';
            setEditingSectionIdx(0);
          }
        }} />
      </div>

      {/* Body: Content + TOC (TOC on right, matching ilouli-work) */}
      <div className="bear-editor-body">
        {/* Content area */}
        <div className="bear-editor-content mode-read">
          {isReadMode ? (
            <div className="pdv-content" style={{ flex: 1, overflow: 'auto', padding: '0 24px 48px' }}>
              {sections.map((section, idx) => (
                <div key={`read-${idx}-${section.charStart}`}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeRaw]}
                    components={markdownComponents}
                  >
                    {section.content || ''}
                  </ReactMarkdown>
                </div>
              ))}
            </div>
          ) : (
            renderEditContent()
          )}
        </div>

        {/* TOC - right side */}
        {headings.length > 0 && (
          <>
            <div className="bear-editor-toc-resize" style={{ visibility: showToc ? 'visible' : 'hidden' }} onMouseDown={handleTocResizeStart} />
            <div
              className={`bear-editor-toc ${showToc ? '' : 'bear-editor-toc-collapsed'}`}
              style={{ width: `${tocWidth}px` }}
            >
              <div className="bear-editor-toc-header" onClick={() => setShowToc(!showToc)}>
                <span className="bear-editor-toc-toggle">{showToc ? '▼' : '▶'}</span>
                <span>목차</span>
              </div>
              {showToc && (
                <nav className="bear-editor-toc-list">
                  {headings.map((h, i) => (
                    <button
                      key={`${h.lineIndex}-${i}`}
                      className="bear-editor-toc-item"
                      style={{ paddingLeft: `${(h.level - 2) * 12 + 8}px` }}
                      onClick={() => handleTocClick(h)}
                      title={h.text}
                    >
                      {h.text}
                    </button>
                  ))}
                </nav>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <ToolBar
        tags={tags}
        onTagsChange={handleTagsChange}
        onDelete={handleDelete}
        onPin={onPin}
        onDuplicate={onDuplicate}
        onExport={onExport}
        saveStatus={saveStatus}
        isPinned={isPinned}
        allTags={allTags}
      />
      <StatusBar content={content} lastSavedAt={lastSavedAt} />

      {/* AI Polish - floating button on text selection */}
      <FloatingPolishButton fullContent={content} onPolished={handleAiPolished} />

      {/* Exit confirm dialog */}
      {showExitConfirm && (
        <div className="exit-confirm-overlay" onClick={() => setShowExitConfirm(false)}>
          <div className="exit-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="exit-confirm-title">변경사항이 있습니다</div>
            <div className="exit-confirm-desc">저장하지 않고 나가면 변경 내용이 사라집니다.</div>
            <div className="exit-confirm-actions">
              <button className="exit-confirm-btn save" onClick={handleExitSave}>저장 후 나가기</button>
              <button className="exit-confirm-btn discard" onClick={handleExitDiscard}>저장하지 않고 나가기</button>
              <button className="exit-confirm-btn cancel" onClick={() => setShowExitConfirm(false)}>취소</button>
            </div>
          </div>
        </div>
      )}

      {/* Restore draft dialog */}
      {showRestorePrompt && (
        <div className="exit-confirm-overlay" onClick={() => { discardedDraftRef.current = null; setShowRestorePrompt(false); }}>
          <div className="exit-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="exit-confirm-title">이전 수정 내역이 있습니다</div>
            <div className="exit-confirm-desc">저장하지 않고 나간 수정 내역을 복원할 수 있습니다.</div>
            <div className="exit-confirm-actions">
              <button className="exit-confirm-btn save" onClick={handleRestoreDraft}>수정 내역 복원</button>
              <button className="exit-confirm-btn discard" onClick={handleDiscardDraft}>현재 버전으로 시작</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
